/**
 * Channels Hook
 * არხების ჰუკი
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getWidgetChannels,
  getChannel,
  connectTelegram,
  connectWhatsApp,
  connectViber,
  connectMessenger,
  connectInstagram,
  updateChannelStatus,
  disconnectChannel,
  type ConnectTelegramRequest,
  type ConnectWhatsAppRequest,
  type ConnectViberRequest,
  type ConnectMessengerRequest,
  type ConnectInstagramRequest,
  type UpdateChannelStatusRequest,
} from '../features/api/channels';

// Get all channels for a widget
export function useWidgetChannels(widgetId: string) {
  const query = useQuery({
    queryKey: ['channels', 'widget', widgetId],
    queryFn: () => getWidgetChannels(widgetId),
    enabled: !!widgetId,
  });

  return {
    channels: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

// Get single channel
export function useChannel(channelId: string) {
  const query = useQuery({
    queryKey: ['channels', channelId],
    queryFn: () => getChannel(channelId),
    enabled: !!channelId,
  });

  return {
    channel: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

// Connect Telegram
export function useConnectTelegram() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ConnectTelegramRequest) => connectTelegram(data),
    onSuccess: (_, { widgetId }) => {
      queryClient.invalidateQueries({ queryKey: ['channels', 'widget', widgetId] });
    },
  });
}

// Connect WhatsApp
export function useConnectWhatsApp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ConnectWhatsAppRequest) => connectWhatsApp(data),
    onSuccess: (_, { widgetId }) => {
      queryClient.invalidateQueries({ queryKey: ['channels', 'widget', widgetId] });
    },
  });
}

// Connect Viber
export function useConnectViber() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ConnectViberRequest) => connectViber(data),
    onSuccess: (_, { widgetId }) => {
      queryClient.invalidateQueries({ queryKey: ['channels', 'widget', widgetId] });
    },
  });
}

// Connect Messenger
export function useConnectMessenger() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ConnectMessengerRequest) => connectMessenger(data),
    onSuccess: (_, { widgetId }) => {
      queryClient.invalidateQueries({ queryKey: ['channels', 'widget', widgetId] });
    },
  });
}

// Connect Instagram
export function useConnectInstagram() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ConnectInstagramRequest) => connectInstagram(data),
    onSuccess: (_, { widgetId }) => {
      queryClient.invalidateQueries({ queryKey: ['channels', 'widget', widgetId] });
    },
  });
}

// Update channel status (enable/disable)
export function useUpdateChannelStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ channelId, data }: { channelId: string; data: UpdateChannelStatusRequest }) =>
      updateChannelStatus(channelId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}

// Disconnect channel
export function useDisconnectChannel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (channelId: string) => disconnectChannel(channelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}
